import { useEffect, useCallback } from "react";

/**
 * A reusable Paystack payment button for React (browser version, no react-paystack dependency)
 *
 * Usage:
 * <PaystackButton
 *   publicKey={PAYSTACK_KEY}
 *   email={user.email}
 *   amount={totalAmountKobo}
 *   metadata={{ custom: 'data' }} // optional
 *   onSuccess={(ref) => { ... }}
 *   onClose={() => { ... }}
 *   children={...}
 * />
 */
function loadPaystackScript() {
  return new Promise((resolve, reject) => {
    if (window.PaystackPop) return resolve();
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export default function PaystackButton({
  publicKey,
  email,
  amount,
  metadata,
  currency = "NGN",
  reference,
  onSuccess,
  onClose,
  children,
  ...props
}) {
  useEffect(() => {
    loadPaystackScript();
  }, []);

  const handlePay = useCallback(async () => {
    await loadPaystackScript();
    if (!window.PaystackPop) {
      alert("Paystack script not loaded.");
      return;
    }
    const refValue = reference || `ref_${Date.now()}`;
    const handler = window.PaystackPop.setup({
      key: publicKey,
      email,
      amount,
      currency,
      ref: refValue,
      metadata: metadata || {},
      callback: function (response) {
        if (onSuccess) onSuccess(response);
      },
      onClose: function () {
        if (onClose) onClose();
      }
    });
    handler.openIframe();
  }, [publicKey, email, amount, metadata, currency, reference, onSuccess, onClose]);

  return (
    <button type="button" onClick={handlePay} {...props}>
      {children || "Pay with Paystack"}
    </button>
  );
}
